import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	type ISupportNotificationSettings,
	useMyAgentConfigsQuery,
	useUpdateMyNotificationPreferencesMutation,
} from '../../../queries/Support/useSupportAgentConfig';
import { useSupportMailboxListQuery } from '../../../queries/Support/useSupportMailboxQuery';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { Dialog, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { settingsStyles as st } from '../../pages/AdminSupportSettingsPage/styles/settingsStyles';
import { supportNotificationPreferencesStyles as styles } from './styles/supportNotificationPreferencesStyles';

type EmailField = 'emailOnAssigned' | 'emailOnMentioned' | 'emailOnTicketActivity';

const COLUMNS: { field: EmailField; heading: string; description: string }[] = [
	{
		field: 'emailOnAssigned',
		heading: 'Assigned',
		description: 'A ticket is assigned to me, or I am unassigned from one',
	},
	{ field: 'emailOnMentioned', heading: 'Mentioned', description: 'Someone mentions me in a note' },
	{
		field: 'emailOnTicketActivity',
		heading: 'Activity',
		description: 'A new reply or note on a ticket assigned to me',
	},
];

type PendingKey = `${string}:${EmailField}`;

export function SupportNotificationPreferencesModal({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { data: mailboxes, isLoading: mailboxesLoading } = useSupportMailboxListQuery({ enabled: open });
	const mailboxList = React.useMemo(() => mailboxes ?? [], [mailboxes]);
	const { configs, isLoading: configsLoading } = useMyAgentConfigsQuery(open ? mailboxList.map(m => m.id) : []);
	const [pending, setPending] = React.useState<Partial<Record<PendingKey, boolean>>>({});

	const mutation = useUpdateMyNotificationPreferencesMutation({
		onError: error => toast.error(`Couldn't save notification settings: ${getApiErrorMessage(error, 'Unknown error')}`),
	});

	// Role-based viewers may act in an inbox but have no roster row where agent preferences can be saved.
	const rows = configs
		.filter(c => c.data && c.data.role !== 'none')
		.map(c => ({
			mailboxId: c.mailboxId,
			name: mailboxList.find(m => m.id === c.mailboxId)?.name ?? 'Inbox',
			settings: c.data?.notifications,
		}));

	// Drop optimistic values once the server response lands, so the two never disagree.
	const savedSignature = configs.map(c => JSON.stringify(c.data?.notifications ?? null)).join('|');
	React.useEffect(() => {
		setPending({});
	}, [savedSignature]);

	const valueOf = (mailboxId: string, field: EmailField, settings?: ISupportNotificationSettings) =>
		pending[`${mailboxId}:${field}`] ?? settings?.[field] ?? false;

	const toggle = (mailboxId: string, field: EmailField, next: boolean) => {
		setPending(current => ({ ...current, [`${mailboxId}:${field}`]: next }));
		mutation.mutate({ mailboxId, settings: { [field]: next } });
	};

	const isLoading = mailboxesLoading || configsLoading;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<div className={css(styles.container)}>
					<DialogHeader>
						<DialogTitle>Email notifications</DialogTitle>
						<div className={css(styles.subtitle)}>
							Each inbox is set separately. In-app notifications stay on and are not affected by these.
						</div>
					</DialogHeader>

					{isLoading ? (
						<div className={css(styles.loading)}>
							<LoadingSpinner type='small' />
						</div>
					) : rows.length === 0 ? (
						<div className={css(styles.notAgent)}>
							You are not on any inbox&apos;s team, so there is nothing to notify you about.
						</div>
					) : (
						<>
							<div className={css(styles.grid)}>
								<span className={css(styles.columnHead)}>Email me when</span>
								{COLUMNS.map(column => (
									<span
										key={column.field}
										className={css(styles.columnHead, styles.columnHeadCentred)}
										title={column.description}
									>
										{column.heading}
									</span>
								))}

								{rows.map(row => (
									<React.Fragment key={row.mailboxId}>
										<span className={css(styles.inboxName)}>{row.name}</span>
										{COLUMNS.map(column => {
											const on = valueOf(row.mailboxId, column.field, row.settings);
											return (
												<span key={column.field} className={css(styles.cell)}>
													<Button
														type='button'
														variant='ghost'
														styles={[st.toggle, on && st.toggleActive]}
														onClick={() => toggle(row.mailboxId, column.field, !on)}
														aria-pressed={on}
														aria-label={`${column.heading} emails for ${row.name}`}
													>
														<span className={css(st.toggleKnob, on && st.toggleKnobActive)} />
													</Button>
												</span>
											);
										})}
									</React.Fragment>
								))}
							</div>

							<div className={css(styles.legend)}>
								{COLUMNS.map(column => (
									<div key={column.field} className={css(styles.legendRow)}>
										<span className={css(styles.legendTerm)}>{column.heading}</span>
										<span className={css(styles.hint)}>{column.description}</span>
									</div>
								))}
							</div>
						</>
					)}

					<DialogFooter>
						<Button variant='secondary' onClick={() => onOpenChange(false)}>
							Done
						</Button>
					</DialogFooter>
				</div>
			</DialogPopup>
		</Dialog>
	);
}
